'use client'

import React, { useEffect, useState } from 'react'
import { I18nextProvider } from 'react-i18next'
import { createWebI18n } from '@/lib/i18n'
import type { WebLocale } from '@/lib/locale'

interface I18nProviderProps {
  locale: WebLocale
  children: React.ReactNode
}

/**
 * Wraps the app in a react-i18next provider. The i18n instance is created
 * once per mount with the server-resolved locale, so the very first render
 * (both on the server and during hydration) already sees the right language
 * — no flash of English.
 *
 * When the user toggles the locale we call a server action that updates the
 * cookie and then `router.refresh()`. The new server render passes a new
 * `locale` prop; the effect below switches the instance's language in place,
 * which notifies react-i18next consumers via subscription (no remount, no
 * provider key hack).
 */
export function I18nProvider({ locale, children }: I18nProviderProps) {
  const [i18n] = useState(() => createWebI18n(locale))

  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale)
    }
  }, [i18n, locale])

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
}
